import { maxBy } from 'es-toolkit';
import React from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { Vector3 } from 'three';

import {
  formatDegrees,
  formatForce,
  formatMoment,
  formatRadiansAsDegrees,
  MaterialPropertyValuePair,
  TooltipIconButton,
} from '@paragon/client-tools';
import {
  AnalogLink,
  AnalogLinkEndResult,
  DesignStandard,
  isTpi1VersionOrLater,
  ResultCurve,
  tpiSection,
} from '@paragon/contract-library';
import { PropertiesPanel, PropertiesSubpanel } from '@paragon/layout-models';
import { isLoggedInUserEngineer } from '@paragon/login-tools/source/scripts/selectors/users';
import { NumericField } from '@paragon/material';

import { getPointFromPolynomial } from '../../../../library/getPolynomialPoint';
import { RootState } from '../../../../store';
import { changeLinkFixity } from '../../../../store/actions/editAnalogModelActions';
import { getObjectCheckFailureSummaryString } from '../../../../store/selectors/analysisSetsSelectors';
import {
  getActiveDeflectionCurves,
  getActiveLinkEndResults,
  getActiveMomentCurves,
  getActiveShearCurves,
} from '../../../../store/selectors/componentForcesSelectors';
import { getActiveAnalysisSet } from '../../../../store/selectors/getActiveAnalysisSetSelector';
import { getAnalysisSettingsForActiveComponentDesign } from '../../../../store/selectors/getAnalysisSettingsForActiveComponentDesign';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { getSelectedAnalysisResult } from '../../../../store/selectors/getSelectedAnalysisResultSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../store/selectors/getSelectedItemSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

interface LinkEndResultsProps {
  readonly link: AnalogLink;
  readonly activeLinkEndResults: readonly AnalogLinkEndResult[] | null;
}

function LinkEndResults({ link, activeLinkEndResults }: LinkEndResultsProps) {
  const forces = [];
  if (activeLinkEndResults) {
    const linkEndResults = activeLinkEndResults.filter((linkRes) => link.guid === linkRes.analogLinkGuid);
    for (let i = 0; i < linkEndResults.length; i += 1) {
      let name = `I (${link.iJoint.name})`;
      if (i > 0) name = `J (${link.jJoint.name})`;
      forces.push(
        <PropertiesSubpanel title={name} key={linkEndResults[i].guid}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Axial Force"
              value={formatForce(linkEndResults[i].axialForce)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Transverse Force"
              value={formatForce(linkEndResults[i].transverseForce)}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="X Force"
              value={formatForce(linkEndResults[i].xForce)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Y Force"
              value={formatForce(linkEndResults[i].yForce)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Moment"
              value={formatMoment(linkEndResults[i].moment)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="AngularDeflection"
              value={formatRadiansAsDegrees(linkEndResults[i].angularDeflection)}
            />
          </Box>
        </PropertiesSubpanel>,
      );
    }
  }
  return <PropertiesSubpanel title="Link End Results">{forces}</PropertiesSubpanel>;
}

interface CurveProps {
  readonly link: AnalogLink;
  readonly curves: readonly ResultCurve[] | null;
  readonly property: string;
  readonly formatter: (value: number) => string;
}

function Curve({ link, curves, property, formatter }: CurveProps) {
  const result = [];
  const iJointVector = new Vector3(link.iJoint.x, link.iJoint.y, link.iJoint.z);
  const jJointVector = new Vector3(link.jJoint.x, link.jJoint.y, link.jJoint.z);
  const linkMagnitude = iJointVector.sub(jJointVector).length();
  const valueCount = 26;
  const valueSpacing = linkMagnitude / (valueCount - 1);
  if (curves) {
    const linkCurve = curves.find((curve) => curve.analogLinkGuid === link.guid);
    if (linkCurve) {
      for (let index = 0; index < valueCount; index += 1) {
        result.push(
          <MaterialPropertyValuePair
            sx={{ m: 1, width: '30%' }}
            property={index.toString()}
            value={formatter(getPointFromPolynomial(valueSpacing * index, linkCurve.coefficients))}
            key={index}
          />,
        );
      }
    }
  }
  return (
    <PropertiesSubpanel title={property}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}>{result}</Box>
    </PropertiesSubpanel>
  );
}

function getReducedWindLoadsForCC(state: RootState) {
  const selectedAnalysisResult = getSelectedAnalysisResult(state);
  return selectedAnalysisResult?.reducedWindLoadsForCC;
}

export function LinkProperties() {
  const link = useAppSelector((state) => getSingleSelectedItemEntryOrNull(state)!.item as AnalogLink);
  const activeLinkEndResults = useAppSelector(getActiveLinkEndResults);
  const momentCurves = useAppSelector(getActiveMomentCurves);
  const deflectionCurves = useAppSelector(getActiveDeflectionCurves);
  const shearCurves = useAppSelector(getActiveShearCurves);
  const isViewOnly = useAppSelector((state) => propertiesShouldBeViewOnly(state) || !isLoggedInUserEngineer(state));
  const analysisResult = useAppSelector(getSelectedAnalysisResult);
  const reducedWindLoadsForCC = useAppSelector(getReducedWindLoadsForCC);
  const componentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const designStandard = useAppSelector(getAnalysisSettingsForActiveComponentDesign)?.designStandard;
  const analysisSet = useAppSelector(getActiveAnalysisSet);
  const checkFailureSummaryString = getObjectCheckFailureSummaryString(link.guid, analysisSet);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const dispatch = useAppDispatch();

  const memberSummary = analysisSet?.memberStressSummaries.find((summary) => summary.linkGuid === link?.guid);
  const maxMoment =
    memberSummary &&
    maxBy(
      [memberSummary.adjustedIEndMoment, memberSummary.adjustedMidPanelMoment, memberSummary.adjustedJEndMoment],
      ({ value }) => Math.abs(value),
    );

  const onChangeIFixity = (newFixity: number) => {
    dispatch(
      changeLinkFixity(componentDesignGuid!, link.guid, { ...link.iFixity, momentRatio: newFixity }, link.jFixity),
    );
  };

  const onChangeJFixity = (newFixity: number) => {
    dispatch(
      changeLinkFixity(componentDesignGuid!, link.guid, link.iFixity, { ...link.jFixity, momentRatio: newFixity }),
    );
  };

  const tpi2022OrNewer =
    designStandard != null && isTpi1VersionOrLater(designStandard, 2022) ? designStandard : DesignStandard.Tpi2022;
  const stiffnessLimitFactorHelpText =
    'The connection stiffness value is limited to the specified percentage of the EI value. This is based on ' +
    `imperial units. For more details, see ${tpiSection('6.1.1.3.2(b,d)', tpi2022OrNewer)}.`;

  const maxVerticalDeflection = analysisResult
    ? (analysisResult.analogLinkMaxVerticalDeflections.find(
        (maxDeflection) => maxDeflection.analogLinkGuid === link.guid,
      )?.deflection ?? 'Link Not Analyzed')
    : 'Load Case not selected / Not a detailed analysis';

  const deflectionCurvesSectionTitle = reducedWindLoadsForCC
    ? 'Deflection Curves* (using C-C scaling factor on wind loads)'
    : 'Deflection Curves';
  const maxVerticalDeflectionSectionTitle = reducedWindLoadsForCC
    ? 'Max Vertical Deflection* (using C-C scaling factor on wind loads)'
    : 'Max Vertical Deflection';

  return (
    <PropertiesPanel title="Link Properties">
      <Stack spacing={2}>
        <MaterialPropertyValuePair
          sx={{ m: 1, flex: 1 }}
          property="Link Length"
          value={distanceFormatter(link.length)}
        />
        <MaterialPropertyValuePair
          sx={{ m: 1, flex: 1 }}
          property="Angle with Horizontal"
          value={formatDegrees(link.angle)}
        />
        <NumericField
          sx={{ m: 1, flex: 1 }}
          value={link.iFixity.momentRatio}
          label={`I Fixity (${link.iJoint.name})`}
          min={0}
          max={1}
          onSubmit={onChangeIFixity}
          disabled={isViewOnly}
        />
        {link.iFixity.stiffnessLimitFactor == null ? null : (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <NumericField
              sx={{ m: 0 }}
              value={link.iFixity.stiffnessLimitFactor * 100}
              label={`I Stiffness Limit Factor (${link.iJoint.name})`}
              endAdornment="%"
              disabled
            />
            <TooltipIconButton title={stiffnessLimitFactorHelpText}>
              <HelpIcon />
            </TooltipIconButton>
          </Box>
        )}
        <NumericField
          sx={{ m: 1, flex: 1 }}
          value={link.jFixity.momentRatio}
          label={`J Fixity (${link.jJoint.name})`}
          min={0}
          max={1}
          onSubmit={onChangeJFixity}
          disabled={isViewOnly}
        />
        {link.jFixity.stiffnessLimitFactor == null ? null : (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <NumericField
              sx={{ m: 0 }}
              value={link.jFixity.stiffnessLimitFactor * 100}
              label={`J Stiffness Limit Factor (${link.jJoint.name})`}
              endAdornment="%"
              disabled
            />
            <TooltipIconButton title={stiffnessLimitFactorHelpText}>
              <HelpIcon />
            </TooltipIconButton>
          </Box>
        )}
        {checkFailureSummaryString !== '' && (
          <MaterialPropertyValuePair
            sx={{ m: 1, flex: 1 }}
            property={`Check Failure Summary`}
            value={checkFailureSummaryString}
          />
        )}
        {memberSummary && (
          <>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`CSI (Load Case ${memberSummary.csi.loadCaseIndex})`}
              value={memberSummary.csi.value}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`SSI (Load Case ${memberSummary.ssi.loadCaseIndex})`}
              value={memberSummary.ssi.value}
            />
            {memberSummary.compression && memberSummary.tension && (
              <MaterialPropertyValuePair
                sx={{ m: 1, flex: 1 }}
                property={`Mid Panel Axial (Load Case ${memberSummary.midPanelAxial.loadCaseIndex})`}
                value={formatForce(memberSummary.midPanelAxial.value)}
              />
            )}
            {memberSummary.compression && (
              <MaterialPropertyValuePair
                sx={{ m: 1, flex: 1 }}
                property={`Compression (Load Case ${memberSummary.compression.loadCaseIndex})`}
                value={formatForce(memberSummary.compression.value)}
              />
            )}
            {memberSummary.tension && (
              <MaterialPropertyValuePair
                sx={{ m: 1, flex: 1 }}
                property={`Tension (Load Case ${memberSummary.tension.loadCaseIndex})`}
                value={formatForce(memberSummary.tension.value)}
              />
            )}
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`I End Moment / K_m (Load Case ${memberSummary.adjustedIEndMoment.loadCaseIndex})`}
              value={formatMoment(memberSummary.adjustedIEndMoment.value)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`J End Moment / K_m (Load Case ${memberSummary.adjustedJEndMoment.loadCaseIndex})`}
              value={formatMoment(memberSummary.adjustedJEndMoment.value)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`Max Moment / K_m (Load Case ${maxMoment!.loadCaseIndex})`}
              value={formatMoment(maxMoment!.value)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`Shear (Load Case ${memberSummary.shear.loadCaseIndex})`}
              value={formatForce(memberSummary.shear.value)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property={`Max Bracing Spacing`}
              value={distanceFormatter(memberSummary.purlinSpacing)}
            />
          </>
        )}
        <MaterialPropertyValuePair
          sx={{ m: 1, flex: 1 }}
          property={`${maxVerticalDeflectionSectionTitle}`}
          value={`${
            typeof maxVerticalDeflection === 'number' ? distanceFormatter(maxVerticalDeflection) : maxVerticalDeflection
          }`}
        />
        {activeLinkEndResults || momentCurves || deflectionCurves || shearCurves ? (
          <>
            <Typography sx={{ m: 'auto' }} variant="subtitle1">
              Force Analysis Data
            </Typography>
            <LinkEndResults link={link} activeLinkEndResults={activeLinkEndResults} />
            <Curve link={link} curves={momentCurves} property="Moment Curves" formatter={formatMoment} />
            <Curve
              link={link}
              curves={deflectionCurves}
              property={deflectionCurvesSectionTitle}
              formatter={distanceFormatter}
            />
            <Curve link={link} curves={shearCurves} property="Shear Curves" formatter={formatForce} />
          </>
        ) : null}
      </Stack>
    </PropertiesPanel>
  );
}
